import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { PageHeader, Container } from '@/components/ui';
import { MainLayout } from '@/layouts/MainLayout';
import { currentUserService } from '@/services/currentUserService';
import type { CurrentUser, Company } from '@/models/CurrentUser';

interface ProfileFormValues {
  name: string;
  email: string;
  whatsAppNumber: string;
  isNotifyMonday: boolean;
  isNotifyTuesday: boolean;
  isNotifyWednesday: boolean;
  isNotifyThursday: boolean;
  isNotifyFriday: boolean;
}

// Validation schema matching Angular implementation
const profileValidationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  whatsAppNumber: Yup.string()
    .matches(/^\+?[1-9_ ][0-9_ ]{7,20}$/, 'Invalid WhatsApp number format')
    .notRequired(),
});

const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const userData = await currentUserService.getMe();
        setUser(userData);
      } catch (error) {
        console.error('Failed to load user profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const initialValues: ProfileFormValues = {
    name: user?.name || '',
    email: user?.email || '',
    whatsAppNumber: user?.whatsAppNumber || '',
    isNotifyMonday: user?.isNotifyMonday || false,
    isNotifyTuesday: user?.isNotifyTuesday || false,
    isNotifyWednesday: user?.isNotifyWednesday || false,
    isNotifyThursday: user?.isNotifyThursday || false,
    isNotifyFriday: user?.isNotifyFriday || false,
  };

  const handleSubmit = async (
    values: ProfileFormValues,
    { setSubmitting }: FormikHelpers<ProfileFormValues>
  ) => {
    try {
      // Format WhatsApp number (remove spaces)
      const formattedValues = {
        ...values,
        whatsAppNumber: values.whatsAppNumber ? values.whatsAppNumber.replace(/\s/g, '') : '',
      };

      await currentUserService.updateMe(formattedValues);
      setUser({ ...user, ...formattedValues } as CurrentUser);
      // Show success toast (assuming you have a toast service)
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Show error toast
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Container>
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-lg text-gray-500 dark:text-gray-400">Loading profile...</div>
          </div>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container>
        <div className="mx-auto w-full max-w-3xl space-y-6">
          <PageHeader title="User Profile" />

          <Card className="mx-auto w-full max-w-3xl">
            <Formik
              initialValues={initialValues}
              validationSchema={profileValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, isSubmitting, setFieldValue }) => (
                <Form>
                  {/* Account Details Section */}
                  <div className="mb-4">
                    <h3 className="-mx-6 mb-6 border-b border-gray-200 px-6 pb-4 text-xl font-semibold text-gray-900 dark:border-dark-600 dark:text-white">
                      Account Details
                    </h3>
                    <div className="space-y-4">
                      <Field
                        as={Input}
                        name="name"
                        label="Full Name"
                        error={touched.name ? errors.name : undefined}
                        required
                      />
                      <Field
                        as={Input}
                        name="email"
                        label="Email"
                        type="email"
                        error={touched.email ? errors.email : undefined}
                        disabled
                        required
                      />
                      <Field
                        as={Input}
                        name="whatsAppNumber"
                        label="Whatsapp Number"
                        placeholder="international format ex: +44 7911 123456"
                        error={touched.whatsAppNumber ? errors.whatsAppNumber : undefined}
                        maxLength={20}
                      />
                      {/* Company Display */}
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Company
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {user?.companies && user.companies.length > 0 ? (
                            user.companies.map((company: Company) => (
                              <Badge key={company.id} variant="primary">
                                {company.name}
                              </Badge>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              No companies assigned
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Notifications Section */}
                  <div className="mb-6">
                    <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
                      Notifications
                    </h3>
                    <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                      Please tick which week days you would like to be notified when the daily
                      report is ready.
                    </p>

                    <div className="space-y-2">
                      <Field name="isNotifyMonday">
                        {({ field }: { field: { name: string; value: boolean } }) => (
                          <Switch
                            name={field.name}
                            checked={field.value}
                            onChange={(checked) => setFieldValue(field.name, checked)}
                            label="Monday"
                          />
                        )}
                      </Field>
                      <Field name="isNotifyTuesday">
                        {({ field }: { field: { name: string; value: boolean } }) => (
                          <Switch
                            name={field.name}
                            checked={field.value}
                            onChange={(checked) => setFieldValue(field.name, checked)}
                            label="Tuesday"
                          />
                        )}
                      </Field>
                      <Field name="isNotifyWednesday">
                        {({ field }: { field: { name: string; value: boolean } }) => (
                          <Switch
                            name={field.name}
                            checked={field.value}
                            onChange={(checked) => setFieldValue(field.name, checked)}
                            label="Wednesday"
                          />
                        )}
                      </Field>
                      <Field name="isNotifyThursday">
                        {({ field }: { field: { name: string; value: boolean } }) => (
                          <Switch
                            name={field.name}
                            checked={field.value}
                            onChange={(checked) => setFieldValue(field.name, checked)}
                            label="Thursday"
                          />
                        )}
                      </Field>
                      <Field name="isNotifyFriday">
                        {({ field }: { field: { name: string; value: boolean } }) => (
                          <Switch
                            name={field.name}
                            checked={field.value}
                            onChange={(checked) => setFieldValue(field.name, checked)}
                            label="Friday"
                          />
                        )}
                      </Field>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card>
        </div>
      </Container>
    </MainLayout>
  );
};

export default ProfilePage;
